import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * 模态框组件 - 用于显示弹出内容
 * @param {boolean} isOpen - 是否显示模态框
 * @param {function} onClose - 关闭模态框的回调函数
 * @param {string} title - 模态框标题
 * @param {node} children - 模态框内容
 * @param {string} size - 模态框大小: 'sm'|'md'|'lg'|'xl'
 * @param {boolean} backdrop - 是否显示背景遮罩
 * @param {boolean} closeOnBackdrop - 点击背景遮罩是否关闭模态框
 * @param {boolean} closeOnEsc - 按ESC键是否关闭模态框
 * @param {node} footer - 模态框底部内容
 * @param {string} className - 额外的CSS类名
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  backdrop = true,
  closeOnBackdrop = true,
  closeOnEsc = true,
  footer,
  className = '',
}) => {
  const modalRef = useRef(null);

  // 处理ESC键关闭
  useEffect(() => {
    const handleEscKey = (event) => {
      if (isOpen && closeOnEsc && event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden'; // 防止背景滚动
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = ''; // 恢复背景滚动
    };
  }, [isOpen, closeOnEsc, onClose]);

  // 处理背景点击
  const handleBackdropClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target) && closeOnBackdrop) {
      onClose();
    }
  };

  // 获取模态框尺寸类名
  const getSizeClass = () => {
    switch (size) {
      case 'sm':
        return 'modal-sm';
      case 'lg':
        return 'modal-lg';
      case 'xl':
        return 'modal-xl';
      case 'md':
      default:
        return '';
    }
  };

  // 如果模态框未打开，则不渲染
  if (!isOpen) return null;

  // 使用Portal渲染到body下，避免CSS样式干扰
  return createPortal(
    <div
      className={`modal fade show d-block ${className}`}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      onClick={backdrop ? handleBackdropClick : undefined}
    >
      {backdrop && <div className="modal-backdrop fade show" style={{ opacity: 0.5 }}></div>}
      
      <div className={`modal-dialog modal-dialog-centered ${getSizeClass()}`} role="document">
        <div className="modal-content" ref={modalRef}>
          {/* 模态框头部 */}
          {title && (
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="关闭"
                onClick={onClose}
              ></button>
            </div>
          )}
          
          {/* 模态框内容 */}
          <div className="modal-body">
            {children}
          </div>
          
          {/* 模态框底部 */}
          {footer && (
            <div className="modal-footer">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

/**
 * 确认框组件 - 用于需要确认的操作
 * @param {boolean} isOpen - 是否显示确认框
 * @param {function} onClose - 关闭确认框的回调函数
 * @param {function} onConfirm - 确认操作的回调函数
 * @param {string} title - 确认框标题
 * @param {node} children - 确认框内容
 * @param {string} confirmText - 确认按钮文本
 * @param {string} cancelText - 取消按钮文本
 * @param {string} confirmVariant - 确认按钮样式: 'primary'|'secondary'|'success'|'danger'|'warning'|'info'
 * @param {boolean} confirmLoading - 确认按钮是否处于加载状态
 */
export const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = '确认操作',
  children,
  confirmText = '确认',
  cancelText = '取消',
  confirmVariant = 'primary',
  confirmLoading = false,
}) => {
  // 自定义底部按钮
  const footer = (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
        disabled={confirmLoading}
      >
        {cancelText}
      </button>
      <button
        type="button"
        className={`btn btn-${confirmVariant}`}
        onClick={onConfirm}
        disabled={confirmLoading}
      >
        {confirmLoading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            处理中...
          </>
        ) : (
          confirmText
        )}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={footer}
      size="sm"
    >
      {children}
    </Modal>
  );
};

export default Modal; 